import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { LoadingScreen } from '@/components/LoadingScreen'
import { createMsalInstance } from '@/lib/msalInstance'
import { detectHost, getTeamsContext, normalizeTeamsTheme, notifyTeamsAppLoaded, prepareTeamsEntry } from '@/lib/teamsHost'
import type { HostKind, TeamsThemeName } from '@/lib/teamsHost'
import type { app as TeamsApp } from '@microsoft/teams-js'
import './styles/design-tokens.css'
import './styles/global.css'

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')
const reactRoot = ReactDOM.createRoot(root)

/**
 * ¿Nos han cargado con una respuesta de autorización en la URL?
 *
 * Una respuesta de MSAL siempre lleva `state` junto a `code`, `error`,
 * `id_token` o `access_token`; exigir el par evita confundirla con una ruta
 * legítima de la aplicación que casualmente traiga `?code=`.
 */
function carriesAuthResponse(): boolean {
  const check = (params: URLSearchParams) =>
    params.has('state') &&
    (params.has('code') ||
      params.has('error') ||
      params.has('id_token') ||
      params.has('access_token'))

  return (
    check(new URLSearchParams(window.location.hash.replace(/^#/, ''))) ||
    check(new URLSearchParams(window.location.search))
  )
}

/**
 * Defensa en profundidad contra el fallo que originó `auth/silent-callback.html`.
 *
 * Con el callback silencioso separado esto no debería dispararse nunca. Se
 * mantiene porque el modo de fallo era silencioso y caro: si un `redirect_uri`
 * vuelve a apuntar a la raíz, la SPA arranca dentro del iframe oculto, pide
 * otro token y recursa hasta agotar el timeout del frame padre.
 *
 * MSAL tiene su propia comprobación equivalente (`blockReloadInHiddenIframes`),
 * pero llega tarde: React Router resuelve `/` con `<Navigate to="/hoy" replace>`
 * antes del primer `acquireTokenSilent`, y ese `replaceState` borra el hash que
 * MSAL necesita para reconocer el iframe. Aquí se comprueba antes de montar
 * nada, cuando la URL todavía está intacta.
 */
if (window.self !== window.top && carriesAuthResponse()) {
  console.warn('[SCC][MSAL] auth response landed on the SPA inside an iframe — not bootstrapping')
} else {
  // Paint the same neutral state that index.html provides before starting any
  // asynchronous Teams/MSAL work. The previous implementation did not mount
  // React until that work finished, leaving the host free to display the prior
  // document for several seconds.
  reactRoot.render(
    <React.StrictMode>
      <LoadingScreen />
    </React.StrictMode>
  )

  bootstrap()
}

/**
 * Bootstrap order matters:
 * 1. Detect the host FIRST — if it's Teams, `app.initialize()` (inside
 *    detectHost) must complete before MSAL is created, so MSAL can see the
 *    Teams nested-app-auth bridge (createNestablePublicClientApplication
 *    only picks it up if teams-js already finished its handshake).
 * 2. Only then create MSAL and mount the authenticated App — mounting the App
 *    first would let components call MSAL on an uninitialized instance and throw
 *    `uninitialized_public_client_application` (blank screen).
 */
async function bootstrap() {
  const host: HostKind = await detectHost()

  let teamsContext: TeamsApp.Context | null = null
  let initialTheme: TeamsThemeName = 'default'
  if (host === 'teams') {
    try {
      teamsContext = await getTeamsContext()
      initialTheme = normalizeTeamsTheme(teamsContext.app.theme)
      prepareTeamsEntry(teamsContext)
    } catch (err) {
      console.error('[SCC][Teams] getContext failed:', err)
    }
  }

  const msalInstance = await createMsalInstance(host).catch((err) => {
    console.error('[SCC][MSAL] initialize failed:', err)
    return null
  })

  if (!msalInstance) {
    reactRoot.render(
      <React.StrictMode>
        <LoadingScreen
          message="No se pudo preparar Sales"
          detail="Actualiza la página para reintentar la conexión con Microsoft 365."
        />
      </React.StrictMode>
    )
    return
  }

  reactRoot.render(
    <React.StrictMode>
      <App msalInstance={msalInstance} host={host} initialTheme={initialTheme} teamsContext={teamsContext} />
    </React.StrictMode>
  )

  if (host === 'teams') notifyTeamsAppLoaded()
}
